import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { openGuide } from "./Guide";
import type { AssistantAnswer, AssistantHit, HelpArticle, NoteInfo } from "@handvero/shared";
import { api, errorText } from "../api";
import { useSettings } from "./settings";
import { Icon, type IconName } from "./ui";

/*
 * Vero, das HandVero-Maskottchen. Man sagt oder schreibt ihm etwas, auch außerhalb eines Projekts:
 * Vero schlägt vor, in welches Projekt es gehört, sucht in den eigenen Daten und beantwortet Fragen zur Bedienung
 * und zum Konto. Alles läuft auf dem HandVero-Server, ohne fremden KI-Dienst. Sprechen geht über das Mikrofon
 * der Handy-Tastatur (Diktierfunktion), dafür wird ebenfalls kein Dienst von uns eingebunden.
 */

type Variant = "desk" | "app";

/** Das Gesicht: ein Quader in der Farbwelt mit Helm und Visier. */
export function VeroFace({ mood = "idle" }: { mood?: "idle" | "think" | "happy" }) {
  return (
    <svg className={`vero-face ${mood}`} viewBox="0 0 64 64" aria-hidden="true">
      <path className="vero-helmet" d="M14 22c0-10 8-16 18-16s18 6 18 16z" />
      <rect className="vero-brim" x="9" y="20" width="46" height="5" rx="2.5" />
      <rect className="vero-body" x="12" y="23" width="40" height="35" rx="14" />
      <rect className="vero-visor" x="18" y="30" width="28" height="17" rx="8.5" />
      <g className="vero-eyes">
        <circle cx="26.5" cy="38.5" r="3" />
        <circle cx="37.5" cy="38.5" r="3" />
      </g>
      <path className="vero-mouth" d="M28.5 51.5q3.5 2.6 7 0" />
    </svg>
  );
}

const STARTERS = ["Wie verknüpfe ich mein Handy?", "Wer sieht welches Projekt?", "Wie lösche ich mein Konto?", "Wie beende ich das Abo?"];

export const KIND_LABEL: Record<AssistantHit["kind"], [string, IconName]> = {
  project: ["Projekt", "grid"],
  folder: ["Ordner", "folder"],
  file: ["Datei", "file"],
  note: ["Notiz", "note"],
  event: ["Termin", "calendar"],
  device: ["Gerät", "clipboard"],
  customer: ["Kunde", "user"],
};

export function hitLink(base: string, h: AssistantHit): string {
  switch (h.kind) {
    case "project":
      return `${base}/p/${h.id}`;
    case "folder":
    case "file":
    case "device":
      return `${base}/p/${h.projectId}/f/${h.folderId}`;
    case "customer":
      return `${base}/kunden`;
    case "note":
      return h.projectId ? `${base}/p/${h.projectId}/notizen` : `${base}/notizen`;
    case "event":
      return h.projectId ? `${base}/p/${h.projectId}/termine` : `${base}/kalender`;
  }
}

function helpLink(variant: Variant, a: HelpArticle): { to: string; label: string } | null {
  const base = variant === "desk" ? "/desktop" : "/app";
  switch (a.area) {
    case "projekte":
      return { to: base, label: "Zu den Projekten" };
    case "notizen":
      return { to: `${base}/notizen`, label: "Zu den Notizen" };
    case "kalender":
      return { to: `${base}/kalender`, label: "Zum Kalender" };
    case "handy":
      return variant === "desk" ? { to: "/desktop/handy", label: "QR-Code zeigen" } : null;
    case "konto":
      return { to: `${base}/konto`, label: "Mein Konto öffnen" };
    case "kundenbereich":
      return { to: "/kundenbereich", label: "Zum Kundenbereich" };
    default:
      return null;
  }
}

export function Mascot({ variant }: { variant: Variant }) {
  const { settings, update } = useSettings();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [asked, setAsked] = useState("");
  const [answer, setAnswer] = useState<AssistantAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<{ projectId: string | null; projectName: string | null } | null>(null);
  const [showSave, setShowSave] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [contactText, setContactText] = useState("");
  const [contactState, setContactState] = useState<"idle" | "busy" | "sent">("idle");
  const contactOpenedAt = useRef(0);
  const input = useRef<HTMLTextAreaElement>(null);
  const base = variant === "desk" ? "/desktop" : "/app";

  // Wer einen Treffer antippt, landet dort; Vero geht dann aus dem Weg.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => input.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!settings.mascot) return null;

  async function ask(q: string) {
    const query = q.trim();
    if (!query || busy) return;
    setBusy(true);
    setError("");
    setSaved(null);
    try {
      const a = await api.get<AssistantAnswer>(`/api/assistant?q=${encodeURIComponent(query)}`);
      setAnswer(a);
      setAsked(query);
      setShowSave(!a.question);
      setText("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function save(projectId: string | null, projectName: string | null) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post<NoteInfo>("/api/notes", { body: asked, projectId });
      setSaved({ projectId, projectName });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void ask(text);
  }

  async function sendContact(e: FormEvent) {
    e.preventDefault();
    setContactState("busy");
    setError("");
    try {
      await api.post("/api/contact", { message: contactText, context: asked || undefined, source: "vero", formMs: Date.now() - contactOpenedAt.current });
      setContactState("sent");
      setContactText("");
    } catch (err) {
      setContactState("idle");
      setError(errorText(err));
    }
  }

  function reset() {
    setContacting(false);
    setContactState("idle");
    setAnswer(null);
    setAsked("");
    setSaved(null);
    setError("");
    input.current?.focus();
  }

  const nothing = answer && !answer.hits.length && !answer.help.length && answer.question;

  return (
    <div className={`hv-vero vero-${variant}${open ? " open" : ""}`}>
      {open && (
        <section className="vero-panel" role="dialog" aria-label="Vero, Ihr Helfer">
          <header className="vero-head">
            <VeroFace mood={busy ? "think" : saved ? "happy" : "idle"} />
            <div>
              <b>Vero</b>
              <small>Sagen oder schreiben Sie mir etwas.</small>
            </div>
            <button className="vero-x" onClick={() => setOpen(false)} aria-label="Vero schließen">
              <Icon name="close" />
            </button>
          </header>

          <div className="vero-main" aria-live="polite">
            {!answer && (
              <>
                <p className="vero-bubble">
                  Ich halte fest, was Sie mir sagen, und schlage das passende Projekt vor. Je genauer Sie Kunde, Straße oder Projekt nennen, desto
                  besser passt mein Vorschlag. Außerdem suche ich in Ihren Projekten, Dateien, Notizen und Terminen und erkläre HandVero.
                </p>
                <div className="vero-starters">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" onClick={() => void ask(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}

            {answer && (
              <>
                <p className="vero-you">{asked}</p>

                {answer.question && answer.help.length > 0 && <HelpList variant={variant} items={answer.help} />}

                {!answer.question && (
                  <SaveBlock answer={answer} saved={saved} busy={busy} base={base} onSave={save} />
                )}

                {answer.hits.length > 0 && (
                  <div className="vero-block">
                    <h4>Gefunden</h4>
                    <ul className="vero-hits">
                      {answer.hits.map((h) => {
                        const [label, icon] = KIND_LABEL[h.kind];
                        return (
                          <li key={`${h.kind}-${h.id}`}>
                            <Link to={hitLink(base, h)}>
                              <span className="vero-hit-ico">
                                <Icon name={icon} />
                              </span>
                              <span className="vero-hit-text">
                                <b>{h.title}</b>
                                <small>
                                  {label} · {h.sub}
                                </small>
                              </span>
                              <Icon name="chev" />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {!answer.question && answer.help.length > 0 && <HelpList variant={variant} items={answer.help.slice(0, 1)} />}

                {nothing && (
                  <p className="vero-bubble">
                    Dazu habe ich leider nichts gefunden. Versuchen Sie es mit einem anderen Wort, zum Beispiel dem Namen des Kunden. Bei allem anderen hilft der
                    Support, schreiben Sie ihm gleich unten.
                  </p>
                )}

                {answer.question && !saved && (
                  <div className="vero-block">
                    {showSave ? (
                      <SaveBlock answer={answer} saved={saved} busy={busy} base={base} onSave={save} />
                    ) : (
                      <button type="button" className="vero-link" onClick={() => setShowSave(true)}>
                        Das war keine Frage? Als Notiz festhalten
                      </button>
                    )}
                  </div>
                )}

                <div className="vero-block">
                  {contactState === "sent" ? (
                    <p className="vero-bubble">Danke, Ihre Nachricht ist beim HandVero-Support. Wir antworten Ihnen per E-Mail.</p>
                  ) : contacting ? (
                    <form className="vero-contact" onSubmit={sendContact}>
                      <h4>Nachricht an den Support</h4>
                      <textarea rows={4} value={contactText} maxLength={4000} onChange={(e) => setContactText(e.target.value)} placeholder="Was möchten Sie uns mitteilen?" aria-label="Nachricht an den Support" />
                      <small>Ihr Name, Ihre E-Mail-Adresse und Ihre Frage an mich werden mitgeschickt, damit wir Ihnen antworten können.</small>
                      <button type="submit" className="vero-send-wide" disabled={contactState === "busy" || contactText.trim().length < 10}>
                        An den Support senden
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className="vero-link"
                      onClick={() => {
                        contactOpenedAt.current = Date.now();
                        setContacting(true);
                      }}
                    >
                      {nothing ? "Nicht weitergekommen? Nachricht an den Support schreiben" : "Das hat nicht geholfen? Nachricht an den Support"}
                    </button>
                  )}
                </div>

                <button type="button" className="vero-link" onClick={reset}>
                  Neu anfangen
                </button>
              </>
            )}
            {error && (
              <p className="vero-error" role="alert">
                {error}
              </p>
            )}
          </div>

          <form className="vero-form" onSubmit={submit}>
            <textarea
              ref={input}
              rows={2}
              value={text}
              maxLength={2000}
              placeholder={answer ? "Noch etwas?" : "z. B. „Bei Schneider fehlt noch der FI im Keller“"}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void ask(text);
                }
              }}
              aria-label="Nachricht an Vero"
            />
            <button type="submit" className="vero-send" disabled={busy || !text.trim()} aria-label="Senden">
              <Icon name="send" />
            </button>
          </form>
          <footer className="vero-foot">
            <span>
              <Icon name="mic" /> Sprechen: Mikrofon auf der Handy-Tastatur antippen.
            </span>
            <button type="button" className="vero-link" onClick={() => update({ mascot: false })}>
              Vero ausblenden
            </button>
          </footer>
        </section>
      )}
      <button
        type="button"
        className="vero-fab"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Vero schließen" : "Vero fragen"}
        title="Vero fragen"
      >
        <VeroFace mood={busy ? "think" : "idle"} />
      </button>
    </div>
  );
}

function SaveBlock(props: {
  answer: AssistantAnswer;
  saved: { projectId: string | null; projectName: string | null } | null;
  busy: boolean;
  base: string;
  onSave: (projectId: string | null, projectName: string | null) => void;
}) {
  const { answer, saved, busy, base } = props;
  if (saved) {
    const to = saved.projectId ? `${base}/p/${saved.projectId}/notizen` : `${base}/notizen`;
    return (
      <div className="vero-block vero-saved">
        <p>
          Erledigt. Ich habe es als Notiz {saved.projectName ? <>im Projekt „{saved.projectName}“</> : "ohne Projekt"} gespeichert.
        </p>
        <Link to={to}>Notiz ansehen</Link>
      </div>
    );
  }
  return (
    <div className="vero-block">
      <h4>{answer.suggestions.length ? "Wohin damit? Mein Vorschlag:" : "Als Notiz festhalten"}</h4>
      <div className="vero-suggest">
        {answer.suggestions.map((s, i) => (
          <button key={s.projectId} type="button" className={i === 0 ? "best" : undefined} disabled={busy} onClick={() => props.onSave(s.projectId, s.projectName)}>
            <b>{s.projectName}</b>
            <small>{s.reason === "zuletzt bearbeitet" ? "zuletzt bearbeitet" : `passt wegen ${s.reason}`}</small>
          </button>
        ))}
        <button type="button" className="plain" disabled={busy} onClick={() => props.onSave(null, null)}>
          <b>Ohne Projekt</b>
          <small>später zuordnen</small>
        </button>
      </div>
    </div>
  );
}

function HelpList({ variant, items }: { variant: Variant; items: HelpArticle[] }) {
  return (
    <div className="vero-block">
      <h4>So geht's</h4>
      {items.map((a) => {
        const link = helpLink(variant, a);
        return (
          <article key={a.id} className="vero-help">
            <b>{a.title}</b>
            <p>{a.text}</p>
            {a.guide && (
              <button className="vero-guide" onClick={() => openGuide(a.guide!)}>
                Mit Bildern zeigen
              </button>
            )}
            {link && <Link to={link.to}>{link.label}</Link>}
          </article>
        );
      })}
    </div>
  );
}
